import React, { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Key } from "@phosphor-icons/react";
import { Logo } from "@/components/common/Logo";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await forgotPassword(email.trim());
      setDone(true);
      setEmailSent(!!r.email_sent);
      // No email provider configured — surface the generated reset link directly.
      if (r.reset_link) setResetLink(r.reset_link);
      toast.success(r.email_sent ? "Reset link sent — check your inbox" : "Reset link generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <Logo variant="horizontal" height={40} />
          <span className="font-head text-2xl font-semibold sr-only">Vozelto</span>
        </div>
        <div className="card-matte p-8" data-testid="forgot-card">
          <div className="flex items-center gap-2 mb-1"><Key size={20} className="brand-text" weight="fill" />
            <h1 className="font-head text-xl font-semibold">Reset your password</h1></div>
          {done ? (
            <div data-testid="forgot-done">
              <p className="text-sm text-[#9a9a9a] mt-2">
                {emailSent
                  ? <>If an account exists for <span className="text-[#cfcfcf]">{email}</span>, we've emailed a secure reset link. It's valid for 1 hour — check your inbox (and spam folder).</>
                  : <>If an account exists for <span className="text-[#cfcfcf]">{email}</span>, a reset link has been generated.</>}
              </p>
              {resetLink && (
                <div className="mt-4 p-3 rounded-lg bg-white/[0.03] border border-white/10">
                  <p className="text-[11px] text-[#7a7a7a] mb-2">No email service is connected yet, so use this secure link to set a new password (valid 1 hour):</p>
                  <Link data-testid="reset-link" to={resetLink} className="text-sm brand-text font-semibold break-all hover:underline">{resetLink}</Link>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-[#9a9a9a] mb-6 mt-1">Enter your email and we'll generate a secure reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="label-caps">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@business.com" className="bg-[#10101A] border-white/10 mt-1" />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">{busy ? "Generating…" : "Send reset link"}</Button>
              </form>
            </>
          )}
          <div className="mt-5">
            <Link data-testid="back-to-login" to="/login" className="text-xs text-[#9a9a9a] hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
